import { Capacitor, registerPlugin } from "@capacitor/core";
import { isHomePath, parseInstallReferrer } from "./app-handoff";

/**
 * Android Play Install Referrer → 크루 클릭 채택(설치 후 첫 실행 1회).
 *
 * 설치 페이지는 Play 스토어 URL 의 referrer 에 "c=<click id>&next=<path>" 를 싣는다.
 * Play 는 그 값을 설치 후 90일간 앱에 돌려준다(InstallReferrerPlugin.java). 첫 실행에서
 * 한 번 읽어 클릭을 앱 WebView 의 la_ref 에 채택하고 보던 상품으로 이어간다.
 *
 * iOS 에는 같은 장치가 없다 — 그쪽은 Safari 쿠키 저장소 + 앱 소셜 로그인 핸드오프가 잇는다.
 */

interface InstallReferrerPlugin {
  getInstallReferrer(): Promise<{ referrer?: string }>;
}

const InstallReferrer = registerPlugin<InstallReferrerPlugin>("InstallReferrer");

/** 채택을 마쳤으면(또는 시도를 다 썼으면) 다시 읽지 않는다(앱 데이터 삭제 시에만 초기화). */
const CHECKED_KEY = "la_install_referrer_checked";
/** 시도한 실행 수. 채택이 끝나지 않은 채(실패·이동으로 중단) 끝난 실행도 센다. */
const ATTEMPTS_KEY = "la_install_referrer_attempts";

/** 이만큼 실행에서 시도해도 안 되면 포기한다(무한 재시도·매 실행 서버 호출 방지). */
export const INSTALL_REFERRER_MAX_ATTEMPTS = 3;

/**
 * 딥링크로 열린 첫 실행에서 채택을 미루는 시간. 그 사이 링크 이동(full reload)이 일어나면
 * 타이머째 사라지고 다음 페이지 로드가 다시 시도한다 — 이동 중에 서버 액션을 띄워
 * 중단시키지 않기 위해서다.
 */
export const INSTALL_REFERRER_DEFER_MS = 4000;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function alreadyChecked(): boolean {
  try {
    return localStorage.getItem(CHECKED_KEY) === "1";
  } catch {
    return true; // 저장소가 없으면 매 실행 재시도하지 않는다.
  }
}

function markChecked(): void {
  write(CHECKED_KEY, "1");
}

/**
 * 이번 실행의 시도를 센다. 상한에 이미 닿았으면 표시를 남기고 false(더 하지 않는다).
 * 시작할 때 세는 이유: 채택 도중 페이지가 이동해 결과를 못 받은 실행도 한 번으로 쳐야
 * 상한이 실제로 걸린다.
 */
function beginAttempt(): boolean {
  const attempts = Number.parseInt(read(ATTEMPTS_KEY) ?? "0", 10) || 0;
  if (attempts >= INSTALL_REFERRER_MAX_ATTEMPTS) {
    markChecked();
    return false;
  }
  write(ATTEMPTS_KEY, String(attempts + 1));
  return true;
}

/**
 * CapacitorBootstrap 이 네이티브 부팅 때 부른다. 절대 던지지 않는다.
 *
 * - 채택(adoptCrewClick)이 **ok 를 돌려준 뒤에만** 끝난 것으로 표시한다. 실패·중단은 다음
 *   실행에서 다시 시도하고, INSTALL_REFERRER_MAX_ATTEMPTS 번째 실행 뒤에는 포기한다.
 * - 크루와 무관한 referrer(오가닉 설치)는 바로 끝난 것으로 표시한다(다시 볼 이유가 없다).
 *
 * @param allowNavigate 딥링크(launch URL)로 이미 다른 화면으로 가는 중이면 false.
 */
export async function runCrewInstallReferrerOnce(allowNavigate: boolean): Promise<void> {
  try {
    if (Capacitor.getPlatform() !== "android") return;
    // 플러그인이 없는 옛 바이너리 — 표시를 남기지 않아, 새 바이너리로 업데이트한 뒤 읽게 둔다.
    if (!Capacitor.isPluginAvailable("InstallReferrer")) return;
    if (alreadyChecked()) return;
    if (!beginAttempt()) return;

    const { referrer } = await InstallReferrer.getInstallReferrer();
    const { clickId, next } = parseInstallReferrer(referrer);
    if (!clickId) {
      markChecked();
      return;
    }

    // 동적 import — 서버 액션 프록시를 부팅 경로에서 정적으로 끌어오지 않는다
    // (oauth-deep-link.ts 의 같은 주석 참조).
    const { adoptCrewClick } = await import("@/app/actions/crew-handoff");
    const result = await adoptCrewClick({ clickId, next });
    if (!result?.ok) {
      console.warn("[crew] install referrer adopt not ok — will retry next launch", result);
      return;
    }
    markChecked();

    // 첫 화면(홈)에 그대로 있을 때만 이어간다 — 사용자가 이미 움직였으면 두지 않는다.
    if (allowNavigate && next && isHomePath(window.location.pathname)) {
      window.location.replace(next);
    }
  } catch (error) {
    console.warn("[crew] install referrer handoff failed (non-fatal):", error);
  }
}

/**
 * 부팅 진입점. 딥링크로 열린 실행(routedByLink)이면 그 이동이 가라앉을 때까지 미룬다 —
 * full reload 면 타이머가 사라지고 다음 페이지 로드가 다시 부른다. 반환값은 타이머 해제 함수.
 */
export function scheduleCrewInstallReferrer(routedByLink: boolean): () => void {
  if (!routedByLink) {
    void runCrewInstallReferrerOnce(true);
    return () => {};
  }
  const timer = setTimeout(() => {
    void runCrewInstallReferrerOnce(false);
  }, INSTALL_REFERRER_DEFER_MS);
  return () => clearTimeout(timer);
}
